import type { UseFormReturn } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { IconButton } from '@mui/material';

import { FormItem } from 'components/shared';
import { TextFieldElement } from 'components/muiInputWrappers';

type Props = {
  form: UseFormReturn<{
    search: string;
  }>;
  setDebouncedSearchTerm: (item: string) => void;
};

/**
 * A component that renders a search input for filtering unread notifications.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.form - The form control object used for managing form state.
 * @param {Function} props.setDebouncedSearchTerm - A function to set the debounced search term.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <SearchUnreadNotificationsInput
 *   form={form}
 *   setDebouncedSearchTerm={setDebouncedSearchTerm}
 * />
 */
function SearchUnreadNotificationsInput({
  form,
  setDebouncedSearchTerm
}: Props): JSX.Element {
  const searchValue = form.watch('search');

  const handleClear = () => {
    form.setValue('search', '');
    setDebouncedSearchTerm('');
  };

  const handleFillSearch = useCallback(() => {
    setDebouncedSearchTerm(searchValue);
  }, [searchValue, setDebouncedSearchTerm]);

  const { t } = useTranslation('global');

  return (
    <Grid marginBottom={2} marginTop={3} xs={12} item>
      <FormItem>
        <TextFieldElement
          control={form.control}
          InputProps={{
            type: 'text',
            endAdornment: (
              <>
                {searchValue && (
                  <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="center"
                    padding="4px"
                    sx={{
                      borderRadius: '50%',
                      '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <CloseIcon
                      fontSize="small"
                      onClick={handleClear}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.54)'
                      }}
                      titleAccess="Clear"
                    />
                  </Box>
                )}
                <IconButton onClick={handleFillSearch}>
                  <SearchIcon
                    sx={{
                      '&:hover': {
                        cursor: 'pointer'
                      }
                    }}
                    type="submit"
                  />
                </IconButton>
              </>
            )
          }}
          name="search"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleFillSearch();
            }
          }}
          placeholder={t('notifications.searchNotification')}
          size="small"
          type="text"
          variant="outlined"
          fullWidth
        />
      </FormItem>
    </Grid>
  );
}

export default SearchUnreadNotificationsInput;
